import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment } from "react";
import { X } from "lucide-react";

const CSVPreviewModal = ({ isOpen, onClose, onConfirm, onCancel, data }) => {
    // Mapping keys to table headers
    const headers = {
        from_pos: "From POS",
        txn_initiated: "Txn Initiated",
        sync_type: "Sync Type",
        to_pos: "To POS",
        txn_reflected: "Txn Reflected",
        trigger: "Trigger",
        sync_interval_value: "Sync Interval (Value)",
        sync_interval_unit: "Sync Interval (Unit)",
    };

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
                <div className="min-h-screen px-4 text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
                    </Transition.Child>

                    <span className="inline-block h-screen align-middle" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <div className="inline-block w-full p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                            <div className="flex justify-between items-center mb-4">
                                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                    Confirm Upload
                                </Dialog.Title>
                                <button
                                    onClick={onCancel}
                                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="mt-2 overflow-x-auto max-h-[60vh] custom-scrollbar border border-gray-200 rounded-md">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-[#0B2B5B]">
                                        <tr>
                                            {Object.values(headers).map((header) => (
                                                <th
                                                    key={header}
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                                                >
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {data.map((row, rowIndex) => (
                                            <tr key={rowIndex} className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                {Object.keys(headers).map((key, colIndex) => (
                                                    <td
                                                        key={`${rowIndex}-${colIndex}`}
                                                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                                                    >
                                                        {row[key] || "--"}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {data.length === 0 && (
                                    <p className="text-center py-4 text-gray-500 text-sm">No valid data found in CSV.</p>
                                )}
                            </div>

                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={onConfirm}
                                    disabled={data.length === 0}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
};

export default CSVPreviewModal;
